import { type SerializedError } from '@reduxjs/toolkit';
import {
  type BaseQueryFn,
  type FetchArgs,
  fetchBaseQuery,
  type FetchBaseQueryError,
  retry,
} from '@reduxjs/toolkit/query';
import { Mutex } from 'async-mutex';

import { APP_DEPLOY_VERSION } from '../config/appVersion';
import SentryService from '../services/SentryService';
import { containsTutorialPatientId } from '../utils/tutorialMode';

const mutex = new Mutex();

type GetAccessTokenSilentlyFn = (options?: {
  detailedResponse?: boolean;
  authorizationParams?: {
    scope?: string;
    audience?: string;
    [key: string]: any;
  };
  cacheMode?: 'on' | 'off' | 'cache-only';
  timeoutInSeconds?: number;
}) => Promise<string>;

let getAccessTokenSilently: GetAccessTokenSilentlyFn | null = null;

export const setAuth0AccessTokenFunction = (tokenFunction: GetAccessTokenSilentlyFn) => {
  getAccessTokenSilently = tokenFunction;
};

/**
 * Get Auth0 access token (for use outside of auth0BaseQuery)
 * Throws if Auth0 is not initialized
 */
export const getAuth0AccessTokenSilently = async (options?: {
  cacheMode?: 'on' | 'off' | 'cache-only';
}) => {
  if (!getAccessTokenSilently) {
    throw new Error('Auth0 not initialized');
  }
  return getAccessTokenSilently(options);
};

/**
 * Check if Auth0 is initialized
 */
export const isAuth0Initialized = () => getAccessTokenSilently !== null;

export interface CustomErrorResponse {
  status: number;
  data: unknown;
  message: string;
}

const handleErrorResponse = (error: FetchBaseQueryError | SerializedError): CustomErrorResponse => {
  if ('status' in error) {
    let message = 'Unknown error occurred';
    const errorData = error.data as Record<string, unknown> | undefined;

    if (typeof errorData === 'object' && errorData) {
      if ('message' in errorData && errorData.message) {
        message = String(errorData.message);
      } else if ('error' in errorData && errorData.error) {
        message = String(errorData.error);
      } else if ('detail' in errorData && errorData.detail) {
        message = String(errorData.detail);
      }
    } else if (typeof error.data === 'string') {
      message = error.data;
    }

    if (message === 'Unknown error occurred') {
      if (error.status === 401) {
        message = 'Authentication failed. Please log in again.';
      } else if (error.status === 403) {
        message = 'You do not have permission to perform this action.';
      } else if (error.status === 404) {
        message = 'The requested resource was not found.';
      } else if (typeof error.status === 'number' && error.status >= 500) {
        message = 'Server error. Please try again later.';
      }
    }

    return {
      status: typeof error.status === 'number' ? error.status : 500,
      data: error.data,
      message,
    };
  }

  return {
    status: 500,
    data: error,
    message: error.message || 'Unknown error occurred',
  };
};

const baseQuery = fetchBaseQuery({
  baseUrl: import.meta.env.VITE_API_URL || 'https://api.smartscribe.com',
  prepareHeaders: headers => {
    headers.set('X-Deploy-Version', APP_DEPLOY_VERSION);
    return headers;
  },
  timeout: 30000,
});

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

let isInitialLoad = true;
setTimeout(() => {
  isInitialLoad = false;
}, 5000);

const AUTH0_INIT_POLL_INTERVAL_MS = 50;
const AUTH0_INIT_MAX_WAIT_MS = 2000;

/**
 * Poll until getAccessTokenSilently becomes available or timeout elapses.
 * Returns true if Auth0 initialized within the window.
 */
const waitForAuth0Init = async (): Promise<boolean> => {
  const deadline = Date.now() + AUTH0_INIT_MAX_WAIT_MS;
  while (!getAccessTokenSilently && Date.now() < deadline) {
    await sleep(AUTH0_INIT_POLL_INTERVAL_MS);
  }
  return getAccessTokenSilently !== null;
};

/**
 * Inject an Authorization header into the request args without mutating
 * the caller's original object.
 */
const withAuthHeader = (args: string | FetchArgs, token: string): FetchArgs => {
  const fetchArgs: FetchArgs =
    typeof args === 'string' ? { url: args } : { ...args };
  const headers = new Headers(fetchArgs.headers as HeadersInit | undefined);
  headers.set('authorization', `Bearer ${token}`);
  fetchArgs.headers = headers;
  return fetchArgs;
};

// Per-endpoint request timeout. RTK Query reads `timeout` from FetchArgs (the
// query args), NOT from `extraOptions`, so an endpoint's `extraOptions.timeout`
// is inert unless merged here. This is additive: endpoints that don't set it keep
// the global 30s on `baseQuery`. Lets a slow endpoint (e.g. denials/leakage) get
// its own longer budget without changing the global cap.
const withTimeout = (args: FetchArgs, timeout?: number): FetchArgs =>
  typeof timeout === 'number' ? { ...args, timeout } : args;

const SESSION_EXPIRED_ERROR = {
  error: {
    status: 401 as const,
    data: { message: 'Session expired. Please log in again.' },
  },
};

/**
 * Acquire the mutex and attempt to obtain a fresh token.
 *
 * 1. After acquiring the lock, re-check the cache — another request that held
 *    the lock before us may have already refreshed it.
 * 2. If the cache is still stale, force-refresh with cacheMode: 'off'.
 * 3. During initial app load, retry once after a delay to tolerate slow Auth0
 *    SDK initialization.
 *
 * Returns a valid token string or null if all attempts fail.
 */
const refreshTokenWithMutex = async (): Promise<string | null> => {
  const release = await mutex.acquire();
  try {
    if (!getAccessTokenSilently) return null;

    // Another request may have refreshed while we waited for the lock
    try {
      return await getAccessTokenSilently();
    } catch {
      // Cache still stale — force refresh
    }

    try {
      return await getAccessTokenSilently({ cacheMode: 'off' });
    } catch (refreshError) {
      console.error('Failed to refresh authentication token:', refreshError);
    }

    if (isInitialLoad) {
      await sleep(1500);
      try {
        return await getAccessTokenSilently({ cacheMode: 'off' });
      } catch (secondError) {
        console.error('Second token refresh attempt failed:', secondError);
      }
    }

    return null;
  } finally {
    release();
  }
};

/**
 * Wait for an in-flight refresh (held by another request) to finish, then
 * read the freshly cached token. Returns null if the token is still unusable.
 */
const waitForRefreshAndGetToken = async (): Promise<string | null> => {
  await mutex.waitForUnlock();
  if (!getAccessTokenSilently) return null;
  try {
    return await getAccessTokenSilently();
  } catch {
    return null;
  }
};

// Enhanced query with token refresh on 401 responses and proper error handling
export const auth0BaseQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError | SerializedError
> = async (args, api, extraOptions) => {
  // Tutorial mode mocks a patient with a non-UUID id; any request that
  // carries it would 500 on the backend's UUID validator. Short-circuit
  // before token acquisition so callsites that forget the per-component
  // `skip: isTutorialMode` guard can't leak. See GDN-1426.
  const argUrl = typeof args === 'string' ? args : args.url;
  const argBody = typeof args === 'string' ? undefined : args.body;
  const argParams = typeof args === 'string' ? undefined : args.params;
  if (containsTutorialPatientId(argUrl, argBody, argParams)) {
    return { data: null };
  }

  await mutex.waitForUnlock();

  // During initial app load, poll-wait for Auth0 SDK to become available
  // instead of a fixed 500ms sleep that may be too short.
  if (isInitialLoad && !getAccessTokenSilently) {
    await waitForAuth0Init();
  }

  // --- Phase 1: acquire a token before touching the network ---
  let token: string | null = null;

  if (getAccessTokenSilently) {
    try {
      token = await getAccessTokenSilently();
    } catch {
      // Token acquisition failed (expired, SDK error, etc.)
      // We'll handle this below — no naked request will be sent.
    }
  }

  // --- Phase 2: if we have no token, short-circuit into the refresh flow ---
  if (!token) {
    if (!mutex.isLocked()) {
      token = await refreshTokenWithMutex();
    } else {
      token = await waitForRefreshAndGetToken();
    }

    if (!token) {
      return SESSION_EXPIRED_ERROR;
    }
  }

  // Per-endpoint timeout override (see withTimeout). Falls back to global 30s.
  const perEndpointTimeout = (extraOptions as { timeout?: number } | undefined)?.timeout;

  // --- Phase 3: execute the request with the token ---
  let result = await baseQuery(
    withTimeout(withAuthHeader(args, token), perEndpointTimeout),
    api,
    extraOptions,
  );

  // --- Phase 4: handle genuine 401 from the server (revoked token, wrong audience, etc.) ---
  if (result.error && 'status' in result.error && result.error.status === 401) {
    let freshToken: string | null = null;

    if (!mutex.isLocked()) {
      freshToken = await refreshTokenWithMutex();
    } else {
      freshToken = await waitForRefreshAndGetToken();
    }

    if (freshToken) {
      result = await baseQuery(
        withTimeout(withAuthHeader(args, freshToken), perEndpointTimeout),
        api,
        extraOptions,
      );
    } else {
      return SESSION_EXPIRED_ERROR;
    }
  }

  // --- Phase 5: format and report errors ---
  if (result.error) {
    const formattedError = handleErrorResponse(result.error);

    if (process.env.NODE_ENV !== 'production') {
      console.error(`API Error (${formattedError.status}):`, formattedError.message, args);
    }

    SentryService.logEvent(`API Error: ${formattedError.message}`, {
      level: 'error',
      tags: {
        type: 'api_error',
        status: formattedError.status.toString(),
      },
      extra: {
        endpoint: typeof args === 'string' ? args : args.url,
        args: JSON.stringify(args),
        method: typeof args === 'string' ? 'GET' : args.method || 'GET',
        errorStatus: formattedError.status,
        errorMessage: formattedError.message,
        errorData: formattedError.data,
      },
    });

    return {
      error: {
        status: formattedError.status,
        data: formattedError,
      },
    };
  }

  return result;
};

export const retryableAuth0BaseQuery = retry(auth0BaseQuery, {
  maxRetries: 3,
});
